import React, { useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ArticleIcon from '@mui/icons-material/Article';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsEthernetOutlinedIcon from '@mui/icons-material/SettingsEthernetOutlined';
import { RootState, AppDispatch } from '../../../../Redux/store';
import {
  lightBackgroundButtonStyles,
  lightBlueBackgroundButtonStyles,
} from '../../../../styles/button';
import SPMetadataModal from '../../../../components/CASBApp/AuthenticationSource/SPMetadataModal';
import ImportIDPMetadata from '../../../../components/CASBApp/AuthenticationSource/ImportIDPMetadata';
import {
  downloadXML,
  generateAndOpenMetadataURL,
} from '../../../../utils/SAMLAuthentication';
import {
  generateMetaData,
  getUser,
  testSAMLConnection,
} from '../../../../utils/utility';
import {
  updateAuthenticationID,
  updateSPMetadata,
} from '../../../../Redux/Slices/Application/AuthenticationSource/authenticationSourceSlice';
import { SubLabel } from '../../../../styles/sublabel';
import { buttonIconStyles } from '../../../../styles/icon';
import { ResponsiveTypography } from '../../../../styles/inputLabel';
import GuideButton from '../../../../components/Buttons/GuideButtons';
import { SmallHeading } from '../../../../styles/smallHeading';

function AuthenticationSourceInformation() {
  const [open, setOpen] = useState(false);
  const [idpOpen, setIdpOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { identifier, _id, spAcsURL } = useSelector(
    (state: RootState) => state.authenticationSource,
  );
  const { app } = useSelector((state: RootState) => state.application);
  const isValidIdentifier = !!identifier;

  const handleIdentifierError = () => {
    toast.error('Please enter valid authentication name.');
  };

  const createHandler = (fn: any) => async () => {
    if (!isValidIdentifier) {
      handleIdentifierError();
      return;
    }
    await fn({ identifier, _id, dispatch });
  };

  const downloadXMLHandler = createHandler(downloadXML);
  const generateAndOpenMetadataURLHandler = createHandler(
    generateAndOpenMetadataURL,
  );

  const copyMetadataURL = async () => {
    if (!isValidIdentifier) {
      handleIdentifierError();
      return;
    }
    const user = await getUser();

    if (!_id) {
      await generateMetaData(identifier)
        .then((data) => {
          dispatch(
            updateAuthenticationID((data as any).success?.sp_metadata?._id),
          );
          dispatch(updateSPMetadata((data as any).success?.sp_metadata?.saml));

          const url = `${process.env.REACT_APP_NODE_BACKEND_URL}/backend/${user?.customer_id}/${user?.current_active_tenant_id}/${(data as any)?.success?.sp_metadata?._id}/generate-sp-metadata`;
          navigator.clipboard.writeText(url);
          toast.success('Copied Metadata URL');
        })
        .catch((error: any) => {
          toast.error(error || 'Failed to copy Metadata URL');
        });
    } else {
      const url = `${process.env.REACT_APP_NODE_BACKEND_URL}/backend/${user?.customer_id}/${user?.current_active_tenant_id}/${_id}/generate-sp-metadata`;
      navigator.clipboard.writeText(url);
      toast.success('Copied Metadata URL');
    }
  };

  const handleGenerateMetaData = () => {
    if (!isValidIdentifier) {
      handleIdentifierError();
      return;
    }
    if (!_id) {
      generateMetaData(identifier)
        .then((data: any) => {
          setOpen(true);
          dispatch(
            updateAuthenticationID((data as any)?.success?.sp_metadata?._id),
          );
          dispatch(updateSPMetadata((data as any).success?.sp_metadata?.saml));
        })
        .catch((error: any) => {
          toast.error(error);
        });
    }
  };
  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={{
          xl: '1rem !important',
          lg: '0.75rem !important',
          md: '0.5rem !important',
        }}
        borderBottom="0.063rem solid #E3E3E3"
        pb={1}
      >
        <SmallHeading>Note</SmallHeading>
        <SubLabel
          sx={{
            color: '#777777',
            fontSize: { xxl: '1.0rem !important', xl: '0.8rem !important' },
          }}
        >
          We support all standard IDPs like&nbsp; miniOrange, Azure AD, Okta,
          Ping, Keycloak etc.&nbsp;
          <a
            href="https://www.miniorange.com/casb/integrations/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here
          </a>
          &nbsp; to view all our integrations.
        </SubLabel>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={{
          xl: '1rem !important',
          lg: '0.75rem !important',
          md: '1rem !important',
        }}
        borderBottom="0.063rem solid #E3E3E3"
        py={{ xl: '1rem', lg: '0.75rem', md: '0.5rem' }}
      >
        <SPMetadataModal open={open} setOpen={setOpen} />
        <ImportIDPMetadata open={idpOpen} setOpen={setIdpOpen} />
        <ResponsiveTypography>SP Metadata</ResponsiveTypography>
        <Box display="flex" flexDirection="row" gap="1rem">
          {!_id ? (
            <Button
              sx={{
                ...lightBackgroundButtonStyles,
                width: {
                  xxl: '11rem !important',
                  xl: '10rem !important',
                  lg: '8rem !important',
                  md: '6rem !important',
                },
              }}
              onClick={handleGenerateMetaData}
            >
              Generate Metadata
            </Button>
          ) : (
            <Button
              sx={{
                ...lightBackgroundButtonStyles,
                width: {
                  xxl: '11rem !important',
                  xl: '10rem !important',
                  lg: '8rem !important',
                  md: '6rem !important',
                },
              }}
              onClick={() => setOpen(true)}
            >
              View Metadata
            </Button>
          )}
          <Button
            sx={{
              ...lightBackgroundButtonStyles,
              width: {
                xxl: '11rem !important',
                xl: '10rem !important',
                lg: '8rem !important',
                md: '6rem !important',
              },
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onClick={generateAndOpenMetadataURLHandler}
          >
            Metadata URL
            <IconButton
              sx={{
                fontSize: '1rem',
                p: 0,
                height: 'auto',
              }}
              onClick={(e) => {
                e.stopPropagation();
                copyMetadataURL();
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Button>
        </Box>
        <Button
          sx={{
            ...lightBackgroundButtonStyles,
            width: {
              xxl: '12rem !important',
              xl: '10rem !important',
              lg: '8rem !important',
              md: '6rem !important',
            },
          }}
          onClick={downloadXMLHandler}
        >
          Download Metadata
        </Button>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={{
          xl: '1rem !important',
          lg: '0.75rem !important',
          md: '1rem !important',
        }}
        borderBottom="0.063rem solid #E3E3E3"
        py="1rem"
      >
        <ResponsiveTypography>IDP Metadata</ResponsiveTypography>
        <Button
          sx={{
            ...lightBackgroundButtonStyles,
            width: {
              xxl: '12rem !important',
              xl: '10rem !important',
              lg: '8rem !important',
              md: '6rem !important',
            },
          }}
          onClick={() => setIdpOpen(true)}
        >
          Upload Metadata
        </Button>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap="1rem"
        borderBottom="0.063rem solid #E3E3E3"
        py="1rem"
      >
        <ResponsiveTypography>Validate Connection</ResponsiveTypography>
        <Box display="flex" flexDirection="row" gap="1rem">
          <Button
            sx={{
              ...lightBlueBackgroundButtonStyles,
              width: {
                xxl: '12rem !important',
                xl: '10rem !important',
                lg: '8rem !important',
                md: '6rem !important',
              },
            }}
            startIcon={(
              <SettingsEthernetOutlinedIcon
                sx={{ ...buttonIconStyles, color: '#0065FF' }}
              />
            )}
            onClick={() => testSAMLConnection(_id, app, spAcsURL)}
            disabled={!_id}
          >
            Test Connection
          </Button>
        </Box>
      </Box>
      <GuideButton
        links={['', '']}
        icons={[
          <YouTubeIcon sx={{ ...buttonIconStyles, color: 'red' }} />,
          <ArticleIcon sx={{ ...buttonIconStyles, color: 'blue' }} />,
        ]}
        labels={['Video Guide', 'Setup Guide']}
      />
    </Box>
  );
}

export default AuthenticationSourceInformation;
